// File: useBinancePairs.ts
// Description: Hook for fetching Binance trading pairs with structured logging

// ─────────────────────────────────────────────────────────────────────────────
// BLOCK 1: IMPORTS AND INTERFACE
// ─────────────────────────────────────────────────────────────────────────────
console.log("1️⃣ [useBinancePairs] Block 1 - Importing dependencies and defining interfaces");

import { useState, useEffect } from 'react';
import { toast } from 'sonner';

interface UseBinancePairsResult {
  coins: string[];
  quoteCurrencies: string[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

console.log("✅ [useBinancePairs] Block 1 completed - Imports and interface defined");

// ─────────────────────────────────────────────────────────────────────────────
// BLOCK 2: MAIN HOOK FUNCTION DEFINITION
// ─────────────────────────────────────────────────────────────────────────────
console.log("2️⃣ [useBinancePairs] Block 2 - Defining main hook function");

/**
 * Custom hook to fetch available trading pairs from Binance based on quote currency
 * @param quoteCurrency The quote currency to filter pairs (e.g., 'USDT', 'BTC', 'ETH')
 * @returns Object containing coins array, loading state, error state, and refetch function
 */
export const useBinancePairs = (quoteCurrency: string): UseBinancePairsResult => {
  console.log(`📥 [useBinancePairs] Hook called with quoteCurrency: "${quoteCurrency}"`);
  
  // ─────────────────────────────────────────────────────────────────────────────
  // SUB-BLOCK 2.1: STATE INITIALIZATION
  // ─────────────────────────────────────────────────────────────────────────────
  console.log("2.1️⃣ [useBinancePairs] Sub-block 2.1 - Initializing state variables");
  
  const [coins, setCoins] = useState<string[]>([]);
  const [quoteCurrencies, setQuoteCurrencies] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  
  console.log("✅ [useBinancePairs] Sub-block 2.1 completed - State initialized");

  // ─────────────────────────────────────────────────────────────────────────────
  // SUB-BLOCK 2.2: DATA FETCHING FUNCTION
  // ─────────────────────────────────────────────────────────────────────────────
  console.log("2.2️⃣ [useBinancePairs] Sub-block 2.2 - Defining fetchBinancePairs function");

  const fetchBinancePairs = async () => {
    // SUB-SUB-BLOCK 2.2.1: PRE-FETCH SETUP
    console.log("2.2.1️⃣ [useBinancePairs] Sub-sub-block 2.2.1 - Starting fetch process");
    setLoading(true);
    setError(null);

    try {
      console.log(`🚀 [useBinancePairs] Fetching pairs for quote currency: ${quoteCurrency || 'all'}`);
      console.log(`📡 [useBinancePairs] API URL: ${import.meta.env.VITE_SUPABASE_URL}/functions/v1/fetch-binance-pairs`);

      // SUB-SUB-BLOCK 2.2.2: API REQUEST
      console.log("2.2.2️⃣ [useBinancePairs] Sub-sub-block 2.2.2 - Making API request");
      
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/fetch-binance-pairs`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          quoteCurrency: quoteCurrency ? quoteCurrency.toUpperCase() : null
        }),
      });

      console.log(`📡 [useBinancePairs] Response status: ${response.status}`);
      console.log(`🔑 [useBinancePairs] Request quoteCurrency: ${quoteCurrency ? quoteCurrency.toUpperCase() : 'null'}`);

      if (!response.ok) {
        const errorData = await response.json();
        console.error(`❌ [useBinancePairs] HTTP error:`, errorData);
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      // SUB-SUB-BLOCK 2.2.3: RESPONSE PROCESSING
      console.log("2.2.3️⃣ [useBinancePairs] Sub-sub-block 2.2.3 - Processing response");
      
      const data = await response.json();
      console.log(`📊 [useBinancePairs] Response received:`, data);

      // Define allowed quote currencies in the desired order
      const allowedQuoteCurrenciesOrder = ['TUSD', 'BNB', 'BTC', 'ETH', 'FDUSD', 'USDC', 'USDT', 'XRP'];
      
      let coinsArray: string[] = [];
      let quoteCurrenciesArray: string[] = [];
      
      // SUB-SUB-BLOCK 2.2.4: DATA VALIDATION AND TRANSFORMATION
      console.log("2.2.4️⃣ [useBinancePairs] Sub-sub-block 2.2.4 - Validating and transforming data");
      
      if (data && typeof data === 'object') {
        console.log(`📦 [useBinancePairs] Response is an object with keys:`, Object.keys(data));
        
        if (Array.isArray(data.quoteCurrencies)) {
          console.log(`📋 [useBinancePairs] Received raw quote currencies:`, data.quoteCurrencies);
          
          // Filter and sort quote currencies based on allowed list
          const filteredQuoteCurrencies = data.quoteCurrencies.filter((qc: string) => 
            allowedQuoteCurrenciesOrder.includes(qc)
          );
          
          // Sort based on the predefined order
          quoteCurrenciesArray = allowedQuoteCurrenciesOrder.filter(qc => 
            filteredQuoteCurrencies.includes(qc)
          );
          
          console.log(`✅ [useBinancePairs] Processed ${quoteCurrenciesArray.length} quote currencies`);
          console.log(`📊 [useBinancePairs] Filtered quote currencies:`, quoteCurrenciesArray);
        }
        
        if (Array.isArray(data.coins)) {
          coinsArray = data.coins;
          console.log(`✅ [useBinancePairs] Received ${coinsArray.length} coins`);
          
          // Log a sample of coins for debugging
          if (coinsArray.length > 0) {
            console.log(`🔍 [useBinancePairs] Sample coins (first 5):`, coinsArray.slice(0, 5));
            // Verify coin format ends with requested quoteCurrency
            if (quoteCurrency) {
              const upperQuote = quoteCurrency.toUpperCase();
              const sampleCoin = coinsArray[0];
              const endsWithQuote = sampleCoin.endsWith(upperQuote);
              console.log(`🔍 [useBinancePairs] Sample coin "${sampleCoin}" ends with "${upperQuote}": ${endsWithQuote}`);
              
              if (!endsWithQuote) {
                console.warn(`⚠️ [useBinancePairs] Coin "${sampleCoin}" doesn't end with quoteCurrency "${upperQuote}"`);
                console.warn(`⚠️ [useBinancePairs] This may cause "Blogas simbolio formatas" error in backend`);
              }
            }
          }
        }
      } else if (Array.isArray(data)) {
        // Backward compatibility - direct array (old format)
        console.log(`🔄 [useBinancePairs] Legacy array format detected`);
        coinsArray = data;
        console.log(`✅ [useBinancePairs] Received direct array with ${coinsArray.length} coins`);
      } else {
        console.error(`❌ [useBinancePairs] Unexpected response format:`, data);
        throw new Error('Expected object with quoteCurrencies and coins arrays from server');
      }

      // SUB-SUB-BLOCK 2.2.5: STATE UPDATE
      console.log("2.2.5️⃣ [useBinancePairs] Sub-sub-block 2.2.5 - Updating state");
      
      setCoins(coinsArray);
      setQuoteCurrencies(quoteCurrenciesArray);
      setError(null);
      
      console.log(`🎉 [useBinancePairs] Successfully processed data. Coins: ${coinsArray.length}, QuoteCurrencies: ${quoteCurrenciesArray.length}`);

    } catch (err: any) {
      // SUB-SUB-BLOCK 2.2.6: ERROR HANDLING
      console.log("2.2.6️⃣ [useBinancePairs] Sub-sub-block 2.2.6 - Handling error");
      
      console.error('❌ [useBinancePairs] Error fetching pairs:', err);
      
      const errorMessage = err.message || 'Failed to fetch trading pairs from Binance';
      setError(errorMessage);
      setCoins([]);
      setQuoteCurrencies([]);
      
      // Show user-friendly toast notification
      if (errorMessage.includes('temporarily unavailable')) {
        toast.error('Binance API is temporarily unavailable. Please try again later.');
      } else if (errorMessage.includes('Network error')) {
        toast.error('Network error. Please check your connection and try again.');
      } else {
        toast.error('No coins found for the selected quote currency. Please try again.');
      }
      
      console.log(`❌ [useBinancePairs] Error state set: ${errorMessage}`);
    } finally {
      // SUB-SUB-BLOCK 2.2.7: CLEANUP
      console.log("2.2.7️⃣ [useBinancePairs] Sub-sub-block 2.2.7 - Final cleanup");
      
      setLoading(false);
      console.log(`🏁 [useBinancePairs] Fetch process completed. Loading: false`);
    }
  };

  console.log("✅ [useBinancePairs] Sub-block 2.2 completed - fetchBinancePairs function defined");

  // ─────────────────────────────────────────────────────────────────────────────
  // BLOCK 3: EFFECT HOOK FOR DATA FETCHING
  // ─────────────────────────────────────────────────────────────────────────────
  console.log("3️⃣ [useBinancePairs] Block 3 - Setting up useEffect for data fetching");

  // Fetch pairs when quote currency changes
  useEffect(() => {
    console.log(`🔄 [useBinancePairs] useEffect triggered with quoteCurrency: "${quoteCurrency}"`);
    console.log(`🔍 [useBinancePairs] Current loading state: ${loading}`);
    
    if (quoteCurrency !== undefined && quoteCurrency !== null) {
      console.log(`✅ [useBinancePairs] Valid quoteCurrency provided, initiating fetch`);
      fetchBinancePairs();
    } else {
      console.warn(`⚠️ [useBinancePairs] quoteCurrency is ${quoteCurrency}, skipping fetch`);
      console.warn(`⚠️ [useBinancePairs] Empty quoteCurrency will cause "Blogas simbolio formatas" error in backend`);
    }
  }, [quoteCurrency]);

  console.log("✅ [useBinancePairs] Block 3 completed - useEffect configured");

  // ─────────────────────────────────────────────────────────────────────────────
  // BLOCK 4: REFETCH FUNCTION
  // ─────────────────────────────────────────────────────────────────────────────
  console.log("4️⃣ [useBinancePairs] Block 4 - Defining refetch function");

  // Refetch function for manual retry
  const refetch = () => {
    console.log(`🔄 [useBinancePairs] Manual refetch triggered for quoteCurrency: "${quoteCurrency}"`);
    console.log(`📊 [useBinancePairs] Current state - Coins: ${coins.length}, QuoteCurrencies: ${quoteCurrencies.length}`);
    
    if (!quoteCurrency) {
      console.error(`❌ [useBinancePairs] Cannot refetch: quoteCurrency is empty!`);
      toast.error('Please select a quote currency before fetching pairs');
      return;
    }
    
    fetchBinancePairs();
  };

  console.log("✅ [useBinancePairs] Block 4 completed - refetch function defined");

  // ─────────────────────────────────────────────────────────────────────────────
  // BLOCK 5: RETURN VALUES
  // ─────────────────────────────────────────────────────────────────────────────
  console.log("5️⃣ [useBinancePairs] Block 5 - Returning hook values");
  
  console.log(`📤 [useBinancePairs] Returning: coins(${coins.length}), quoteCurrencies(${quoteCurrencies.length}), loading(${loading}), error(${error})`);
  
  const result = {
    coins,
    quoteCurrencies,
    loading,
    error,
    refetch
  };
  
  console.log("🏁 [useBinancePairs] Hook execution completed");
  console.log("──────────────────────────────────────────────────────────────────────────────");
  
  return result;
};

console.log("✅ [useBinancePairs] Main hook definition completed");

// ─────────────────────────────────────────────────────────────────────────────
// BLOCK 6: DEFAULT EXPORT
// ─────────────────────────────────────────────────────────────────────────────
console.log("6️⃣ [useBinancePairs] Block 6 - Setting default export");

export default useBinancePairs;

console.log("✅ [useBinancePairs] File fully processed and ready for use");